import { useMemo } from 'react';
import { useTheme } from './useTheme';
import type { Theme } from './theme';

/**
 * Memoized style factory hook.
 *
 * The factory is re-evaluated only when the theme changes (e.g. dark/light toggle).
 * Works identically on web (returns CSSProperties objects) and native
 * (factory can call StyleSheet.create() internally).
 *
 * @example Web
 *   const styles = useStyles(t => ({
 *     container: { background: t.bg, color: t.text, padding: 16 },
 *     btn: { background: t.primary, color: t.primaryText },
 *   }));
 *
 * @example Native
 *   const styles = useStyles(t => StyleSheet.create({
 *     container: { flex: 1, backgroundColor: t.bg },
 *   }));
 */
export function useStyles<T>(factory: (theme: Theme) => T): T {
  const theme = useTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => factory(theme), [theme]);
}
